import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import type { Insight } from '@claude-code-insights/shared';
import { fetchInsight } from '../lib/api';

export function InsightView() {
  const { id } = useParams<{ id: string }>();
  const [insight, setInsight] = useState<Insight | null>(null);
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchInsight(id)
      .then((result) => {
        setInsight(result.insight);
        setMarkdown(result.markdownContent);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="p-6 text-center text-obsidian-text-muted">Loading...</div>
    );
  }

  if (!insight) {
    return (
      <div className="p-6 text-center">
        <p className="text-obsidian-text-muted">Insight not found</p>
        <Link to="/" className="text-obsidian-accent text-sm mt-2 inline-block">
          Back to feed
        </Link>
      </div>
    );
  }

  // Strip frontmatter and AI-generated sections from markdown, keep only original content
  const originalContent = markdown
    .replace(/^---[\s\S]*?---\s*/m, '') // strip YAML frontmatter
    .replace(/## Summary[\s\S]*?(?=## |$)/, '') // strip Summary section
    .replace(/## Actionable Steps[\s\S]*?(?=## |$)/, '') // strip Actionable Steps section
    .replace(/## Related Topics[\s\S]*?(?=## |$)/, '') // strip Related Topics section
    .replace(/## Original Content\s*\n?/, '') // strip the heading itself
    .trim();

  return (
    <div className="flex h-full">
      {/* Main content */}
      <div className="flex-1 overflow-auto p-6 max-w-3xl">
        <Link
          to="/"
          className="text-sm text-obsidian-text-muted hover:text-obsidian-text mb-4 inline-block"
        >
          ← Back to feed
        </Link>

        <h1 className="text-xl font-semibold mt-2 mb-6">{insight.title}</h1>

        {/* Summary */}
        <div className="border-l-2 border-obsidian-accent pl-4 py-2 mb-6 bg-obsidian-accent/5 rounded-r">
          <p className="text-sm text-obsidian-text leading-relaxed">{insight.summary}</p>
        </div>

        {/* Actionable Steps */}
        {insight.actionableSteps.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-obsidian-text-muted uppercase tracking-wide mb-3">
              Actionable Steps
            </h2>
            <ol className="space-y-2">
              {insight.actionableSteps.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-obsidian-accent/20 text-obsidian-accent flex items-center justify-center text-xs font-medium">
                    {i + 1}
                  </span>
                  <span className="text-obsidian-text leading-relaxed pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Original Content (collapsible) */}
        {originalContent && (
          <details className="mb-6 group">
            <summary className="text-sm font-semibold text-obsidian-text-muted uppercase tracking-wide cursor-pointer hover:text-obsidian-text select-none mb-3">
              Original Content
            </summary>
            <div className="prose prose-invert prose-sm max-w-none border-t border-obsidian-border pt-4 text-obsidian-text-muted">
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {originalContent}
              </ReactMarkdown>
            </div>
          </details>
        )}
      </div>

      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 border-l border-obsidian-border p-4 overflow-auto">
        {/* Relevance */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-1">
            Relevance
          </h4>
          <span
            className={`text-2xl font-bold ${
              insight.relevanceScore >= 8
                ? 'text-obsidian-success'
                : insight.relevanceScore >= 5
                  ? 'text-obsidian-accent'
                  : 'text-obsidian-warning'
            }`}
          >
            {insight.relevanceScore}/10
          </span>
        </div>

        {/* Category */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-1">
            Category
          </h4>
          <span className="badge bg-obsidian-accent/20 text-obsidian-accent">
            {insight.category}
          </span>
        </div>

        {/* Source */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-1">
            Source
          </h4>
          <p className="text-sm text-obsidian-text-muted">{insight.sourceType}</p>
          <p className="text-sm text-obsidian-text-muted">{insight.sourceAuthor}</p>
          {insight.sourceUrl && (
            <a
              href={insight.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-obsidian-link hover:underline mt-1 inline-block"
            >
              Open original →
            </a>
          )}
        </div>

        {/* Tags */}
        {insight.tags.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-2">
              Tags
            </h4>
            <div className="flex gap-1 flex-wrap">
              {insight.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 text-xs bg-obsidian-bg-tertiary text-obsidian-text-muted rounded cursor-pointer hover:bg-obsidian-surface"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Date */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-obsidian-text-faint uppercase tracking-wide mb-1">
            Added
          </h4>
          <p className="text-sm text-obsidian-text-muted">
            {new Date(insight.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
      </aside>
    </div>
  );
}
